import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './BlogForm.css';
import { blogAPI } from '../../../api';

const BlogForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: '',
        author: '',
        published_date: '',
        is_published: false,
        image_url: '' // Changé de 'image' à 'image_url'
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEditMode) {
            fetchBlog();
        }
        // Charger les catégories existantes depuis les blogs
        loadCategories();
    }, [id]);

    const fetchBlog = async () => {
        try {
            setLoading(true);
            const response = await blogAPI.getById(id);
            
            if (response.success && response.data) {
                const blog = response.data;
                setFormData({
                    title: blog.title || '',
                    content: blog.content || '',
                    category: blog.category || '',
                    author: blog.author || '',
                    published_date: blog.published_date || '',
                    is_published: blog.is_published || false,
                    image_url: blog.image_url || blog.image || '' // Prendre image_url ou image
                });
            }
        } catch (error) {
            console.error('Erreur lors du chargement du blog:', error);
            alert(error.message || 'Erreur lors du chargement du blog');
            navigate('/admin/blogs');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await blogAPI.getAll();
            if (response.success && response.data) {
                const allCategories = response.data.data
                    .map(blog => blog.category)
                    .filter(cat => cat && cat.trim() !== '')
                    .filter((cat, index, self) => self.indexOf(cat) === index);
                setCategories(allCategories);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des catégories:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Effacer l'erreur pour ce champ
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.title.trim()) {
            newErrors.title = 'Le titre est requis';
        } else if (formData.title.length > 255) {
            newErrors.title = 'Le titre ne doit pas dépasser 255 caractères';
        }
        
        if (!formData.content.trim()) {
            newErrors.content = 'Le contenu est requis';
        }
        
        if (formData.image_url && !isValidUrl(formData.image_url)) {
            newErrors.image_url = "L'URL de l'image n'est pas valide";
        }
        
        if (formData.category && formData.category.length > 100) {
            newErrors.category = 'La catégorie ne doit pas dépasser 100 caractères';
        }
        
        if (formData.author && formData.author.length > 255) {
            newErrors.author = "Le nom de l'auteur ne doit pas dépasser 255 caractères";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Fonction de validation d'URL
    const isValidUrl = (string) => {
        if (!string) return true; // Champ optionnel
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            alert('Veuillez corriger les erreurs dans le formulaire');
            return;
        }
        
        try {
            setSaving(true);
            
            // Préparer les données à envoyer
            const blogData = {
                title: formData.title,
                content: formData.content,
                is_published: formData.is_published,
                image_url: formData.image_url || null // Envoyer null si vide
            };
            
            // Ajouter les champs optionnels seulement s'ils ont des valeurs
            if (formData.category) blogData.category = formData.category;
            if (formData.author) blogData.author = formData.author;
            if (formData.published_date) blogData.published_date = formData.published_date;
            
            console.log('Données envoyées:', blogData);
            
            let response;
            if (isEditMode) {
                response = await blogAPI.update(id, blogData);
            } else {
                response = await blogAPI.create(blogData);
            }
            
            if (response.success) {
                alert(`Blog ${isEditMode ? 'modifié' : 'créé'} avec succès !`);
                navigate('/admin/blogs');
            } else {
                throw new Error(response.message || 'Une erreur est survenue');
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            
            if (error.errors) {
                // Gestion des erreurs de validation du serveur
                setErrors(error.errors);
                alert('Veuillez corriger les erreurs dans le formulaire');
            } else {
                alert(error.message || 'Erreur lors de la sauvegarde du blog');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="blog-form-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Chargement du blog...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-form-container">
            <div className="form-header">
                <h1>{isEditMode ? 'Modifier le Blog' : 'Nouveau Blog'}</h1>
                <Link to="/admin/blogs" className="btn btn-back">
                    ← Retour à la liste
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="blog-form">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="title">
                            Titre du Blog *
                            {errors.title && <span className="error-message"> - {errors.title}</span>}
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className={errors.title ? 'error' : ''}
                            placeholder="Entrez le titre du blog"
                            maxLength="255"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="category">
                            Catégorie
                            {errors.category && <span className="error-message"> - {errors.category}</span>}
                        </label>
                        <div className="category-container">
                            <input
                                type="text"
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                className={errors.category ? 'error' : ''}
                                placeholder="Ex: Technologie, Éducation..."
                                list="categories-list"
                                maxLength="100"
                            />
                            <datalist id="categories-list">
                                {categories.map((cat, index) => (
                                    <option key={index} value={cat} />
                                ))}
                            </datalist>
                        </div>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="author">
                            Auteur
                            {errors.author && <span className="error-message"> - {errors.author}</span>}
                        </label>
                        <input
                            type="text"
                            id="author"
                            name="author"
                            value={formData.author}
                            onChange={handleInputChange}
                            className={errors.author ? 'error' : ''}
                            placeholder="Nom de l'auteur"
                            maxLength="255"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="published_date">Date de publication</label>
                        <input
                            type="date"
                            id="published_date"
                            name="published_date"
                            value={formData.published_date}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="image_url">
                        URL de l'image de couverture
                        {errors.image_url && <span className="error-message"> - {errors.image_url}</span>}
                    </label>
                    <input
                        type="url"
                        id="image_url"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleInputChange}
                        className={errors.image_url ? 'error' : ''}
                        placeholder="https://exemple.com/image.jpg"
                    />
                    <p className="input-help">
                        Entrez l'URL complète de l'image (hébergée sur un service comme Imgur, Cloudinary, etc.)
                    </p>
                </div>

                {formData.image_url && (
                    <div className="image-preview">
                        <img 
                            src={formData.image_url} 
                            alt="Aperçu" 
                            onError={(e) => {
                                e.target.src = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect width="300" height="200" fill="#f8f9fa"/><text x="150" y="100" font-family="Arial" font-size="14" text-anchor="middle" fill="#6c757d">Image non disponible</text></svg>')}`;
                            }}
                        />
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="content">
                        Contenu *
                        {errors.content && <span className="error-message"> - {errors.content}</span>}
                    </label>
                    <textarea
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        className={errors.content ? 'error' : ''}
                        placeholder="Contenu du blog..."
                        rows="15"
                    />
                    <div className="char-count">
                        {formData.content.length} caractères
                    </div>
                </div>

                <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="is_published"
                            checked={formData.is_published}
                            onChange={handleInputChange}
                            className="checkbox-input"
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">Publier immédiatement</span>
                    </label>
                    <p className="checkbox-help">
                        Si décoché, le blog sera enregistré comme brouillon
                    </p>
                </div>

                <div className="form-actions">
                    <Link to="/admin/blogs" className="btn btn-cancel">
                        Annuler
                    </Link>
                    <button 
                        type="submit" 
                        className="btn btn-submit"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <span className="spinner-small"></span>
                                {isEditMode ? 'Modification...' : 'Création...'}
                            </>
                        ) : (
                            <>
                                <span className="btn-icon">💾</span>
                                {isEditMode ? 'Modifier le Blog' : 'Créer le Blog'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BlogForm;